import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { isSellerOtaTestingEnabled } from "@/lib/native/runtime";

export const SELLER_OTA_MANIFEST_URL =
  "https://raw.githubusercontent.com/trioty/triory-app-releases/testing/seller/manifest.json";
export const SELLER_OTA_PUBLIC_KEY_SPKI = "MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAErXx1x1U7/n14Z5noUoHXmToLQG//og7TlNonmUo6pNc5dG5KXZxXUzhUmyKf8spjLjUqculbaavpbqSRE45oWg==";
export const SELLER_OTA_MAX_BUNDLE_BYTES = 50 * 1024 * 1024;

const EXPECTED_APP_ID = "com.trioty.seller";
const EXPECTED_CHANNEL = "testing";
const EXPECTED_ALGORITHM = "ECDSA_P256_SHA256";
const EXPECTED_BUNDLE_PREFIX =
  "/trioty/triory-app-releases/testing/seller/bundles/";

export type SellerOtaPayload = {
  schemaVersion: 1;
  channel: "testing";
  appId: "com.trioty.seller";
  version: string;
  nativeRevision: string;
  bundleUrl: string;
  bundleSha256: string;
  bundleSize: number;
  sourceCommit: string;
  publishedAt: string;
};

type SellerOtaEnvelope = {
  algorithm: string;
  payload: string;
  signature: string;
};

type SellerOtaPolicy = {
  currentVersion: string;
  nativeRevision: string;
  publicKeySpki: string;
};

type SellerOtaUpdater = Pick<
  typeof CapacitorUpdater,
  "download" | "next" | "notifyAppReady"
>;

export type SellerOtaCheckResult =
  | { state: "disabled" | "current" | "incompatible" | "staged"; version?: string }
  | { state: "error"; detail: string };

type SellerOtaCheckOptions = {
  enabled?: boolean;
  fetcher?: typeof fetch;
  manifestUrl?: string;
  policy?: SellerOtaPolicy;
  updater?: SellerOtaUpdater;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function base64Bytes(value: string): Uint8Array<ArrayBuffer> {
  if (value.length === 0 || value.length > 32_768) {
    throw new Error("invalid OTA base64 field");
  }
  const decoded = atob(value);
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
}

function numericVersionParts(version: string): number[] | null {
  return /^\d+\.\d+\.\d+$/.test(version) ? version.split(".").map(Number) : null;
}

export function isSellerOtaVersionNewer(available: string, current: string): boolean {
  const next = numericVersionParts(available);
  const running = numericVersionParts(current);
  if (!next || !running) return false;

  for (let index = 0; index < 3; index += 1) {
    if (next[index] !== running[index]) return (next[index] ?? 0) > (running[index] ?? 0);
  }
  return false;
}

function validatePayload(value: unknown): SellerOtaPayload {
  if (!isRecord(value)) throw new Error("invalid OTA payload");

  const payload = value as Partial<SellerOtaPayload>;
  if (
    payload.schemaVersion !== 1 ||
    payload.channel !== EXPECTED_CHANNEL ||
    payload.appId !== EXPECTED_APP_ID ||
    typeof payload.version !== "string" ||
    !numericVersionParts(payload.version) ||
    typeof payload.nativeRevision !== "string" ||
    !/^[a-f0-9]{12}$/.test(payload.nativeRevision) ||
    typeof payload.bundleSha256 !== "string" ||
    !/^[a-f0-9]{64}$/.test(payload.bundleSha256) ||
    !Number.isSafeInteger(payload.bundleSize) ||
    (payload.bundleSize ?? 0) <= 0 ||
    (payload.bundleSize ?? 0) > SELLER_OTA_MAX_BUNDLE_BYTES ||
    typeof payload.sourceCommit !== "string" ||
    !/^[a-f0-9]{40}$/.test(payload.sourceCommit) ||
    typeof payload.publishedAt !== "string" ||
    Number.isNaN(Date.parse(payload.publishedAt)) ||
    typeof payload.bundleUrl !== "string"
  ) {
    throw new Error("invalid OTA payload fields");
  }

  const bundleUrl = new URL(payload.bundleUrl);
  const expectedName = `seller-web-v${payload.version}.zip`;
  if (
    bundleUrl.protocol !== "https:" ||
    bundleUrl.hostname !== "raw.githubusercontent.com" ||
    bundleUrl.pathname !== `${EXPECTED_BUNDLE_PREFIX}${expectedName}` ||
    bundleUrl.search !== "" ||
    bundleUrl.hash !== ""
  ) {
    throw new Error("untrusted OTA bundle URL");
  }

  return payload as SellerOtaPayload;
}

export async function verifySellerOtaEnvelope(
  value: unknown,
  publicKeySpki = SELLER_OTA_PUBLIC_KEY_SPKI,
): Promise<SellerOtaPayload> {
  if (!isRecord(value)) throw new Error("invalid OTA manifest");
  const envelope = value as Partial<SellerOtaEnvelope>;
  if (
    envelope.algorithm !== EXPECTED_ALGORITHM ||
    typeof envelope.payload !== "string" ||
    typeof envelope.signature !== "string"
  ) {
    throw new Error("invalid OTA manifest envelope");
  }

  const payloadBytes = base64Bytes(envelope.payload);
  const signatureBytes = base64Bytes(envelope.signature);
  const key = await crypto.subtle.importKey(
    "spki",
    base64Bytes(publicKeySpki),
    { name: "ECDSA", namedCurve: "P-256" },
    false,
    ["verify"],
  );
  const verified = await crypto.subtle.verify(
    { name: "ECDSA", hash: "SHA-256" },
    key,
    signatureBytes,
    payloadBytes,
  );
  if (!verified) throw new Error("invalid OTA manifest signature");

  return validatePayload(JSON.parse(new TextDecoder().decode(payloadBytes)));
}

function defaultPolicy(): SellerOtaPolicy {
  return {
    currentVersion: import.meta.env.VITE_SELLER_OTA_VERSION ?? "1.0.0",
    nativeRevision: import.meta.env.VITE_NATIVE_REVISION ?? "dev",
    publicKeySpki: SELLER_OTA_PUBLIC_KEY_SPKI,
  };
}

export async function checkSellerOta(
  options: SellerOtaCheckOptions = {},
): Promise<SellerOtaCheckResult> {
  const enabled = options.enabled ?? isSellerOtaTestingEnabled();
  if (!enabled) return { state: "disabled" };

  const policy = options.policy ?? defaultPolicy();
  if (!/^[a-f0-9]{12}$/.test(policy.nativeRevision)) {
    return { state: "error", detail: "invalid embedded native revision" };
  }

  try {
    const response = await (options.fetcher ?? fetch)(
      options.manifestUrl ?? SELLER_OTA_MANIFEST_URL,
      {
        cache: "no-store",
        headers: { Accept: "application/json" },
      },
    );
    if (!response.ok) throw new Error(`manifest HTTP ${response.status}`);

    const manifestText = await response.text();
    if (manifestText.length > 32_768) throw new Error("OTA manifest is too large");
    const payload = await verifySellerOtaEnvelope(
      JSON.parse(manifestText) as unknown,
      policy.publicKeySpki,
    );

    if (payload.nativeRevision !== policy.nativeRevision) {
      return { state: "incompatible", version: payload.version };
    }
    if (!isSellerOtaVersionNewer(payload.version, policy.currentVersion)) {
      return { state: "current", version: payload.version };
    }

    const updater = options.updater ?? CapacitorUpdater;
    const bundle = await updater.download({
      version: payload.version,
      url: payload.bundleUrl,
      checksum: payload.bundleSha256,
    });
    if (
      bundle.checksum &&
      bundle.checksum.toLowerCase() !== payload.bundleSha256
    ) {
      throw new Error("downloaded OTA checksum mismatch");
    }
    await updater.next({ id: bundle.id });
    return { state: "staged", version: payload.version };
  } catch (error) {
    return {
      state: "error",
      detail: error instanceof Error ? error.message : "OTA check failed",
    };
  }
}

export async function confirmSellerOtaBoot(
  updater: SellerOtaUpdater = CapacitorUpdater,
): Promise<void> {
  if (!isSellerOtaTestingEnabled()) return;
  await updater.notifyAppReady();
}

